import { useEffect, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getNodeShareList,
  getPublicAsset,
  getPublicMultipleAssets,
  getPublicShare,
  getShare,
} from '@/services/share.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { reloadPage } from '@/utilities/browser';
import { type SortState } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';
import { useRouter } from 'next/router';

// BE returns 401 from the share route binder for any anonymous viewer
// hitting a private share. These endpoints are unauthenticated by default,
// so a 401 on this surface unambiguously means "private share, sign in."
// Returned by the paired BE change (LKBKS/dam-backend PR).
const isPrivateShareAuthRequired = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 401;

// A guest whose access to a share is revoked mid-session gets a 404 from the
// asset/multi-asset endpoints on their next fetch (the share binder can no
// longer resolve them onto it).
const isNotFound = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 404;

const buildIdentifierRedirectUrl = () =>
  `/identifier?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;

// Guarded against authIsLoading so we don't redirect a user mid-bootstrap
// (isAuthenticated reports false while Auth0 is still resolving the session).
const shouldRedirectToIdentifier = (error: unknown, isAuthenticated: boolean, authIsLoading: boolean): boolean =>
  !authIsLoading && !isAuthenticated && isPrivateShareAuthRequired(error);

// Only reloads on a 404 that follows a previously successful fetch — i.e. access
// was revoked mid-session — not on a first-load 404, which is a genuine not-found
// and should render the normal NotFound UI instead of reload-looping forever.
// Checks error before data: TanStack Query keeps the last-successful `data`
// around even after a failed refetch, so a truthy `data` can't be used to mean
// "this fetch succeeded".
const useReloadOnAccessRevoked = (data: unknown, error: unknown): void => {
  const hadDataRef = useRef(false);

  useEffect(() => {
    if (isNotFound(error)) {
      if (hadDataRef.current) {
        reloadPage();
      }
      return;
    }
    if (data) {
      hadDataRef.current = true;
    }
  }, [data, error]);
};

export const useNodeSharesList = (
  params: {
    nodeId: string;
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
    queryString: string;
  },
  options?: object,
) => {
  const shareListQueryFn = useAuthenticatedQueryFn(getNodeShareList);

  const {
    data: shareList = { shares: [], pagination: { count: 0, limit: 0, offset: 0 } },
    isPending: shareListIsPending,
    isFetching: shareListIsFetching,
    error: shareListError,
    refetch: refetchShareList,
  } = useQuery({
    queryKey: [QueryKey.NodeShareList, params.nodeId, params.pagination, params.queryString, params.sort],
    queryFn: () => shareListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    shareList,
    shareListIsFetching,
    shareListIsPending,
    shareListError,
    refetchShareList,
  };
};

export const useShare = (params: { nodeId: string; shareId: string }, options: object, form: any) => {
  const getShareQueryFn = useAuthenticatedQueryFn(getShare);

  const {
    data: share = [],
    isPending: shareIsPending,
    isFetching: shareIsFetching,
    error: shareError,
    refetch: refetchShare,
  } = useQuery({
    queryKey: [QueryKey.GetShare, params.nodeId, params.shareId],
    queryFn: async () => {
      const data = await getShareQueryFn(params);
      form?.reset(data);
      return data;
    },
    ...options,
  });

  return {
    share,
    shareIsPending,
    refetchShare,
    shareIsFetching,
    shareError,
  };
};

export const usePublicShare = (
  shareId: string,
  pagination: { offset: number; limit: number },
  sort: SortState,
  queryString: string,
  assetId?: string,
  options?: object,
) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authIsLoading } = useAuth0();
  const { push } = useRouter();
  const {
    data,
    isPending: shareIsPending,
    isFetching: shareIsFetching,
    isLoading: shareIsLoading,
    isFetched: shareIsFetched,
    error: shareError,
    refetch: refetchShare,
  } = useQuery({
    queryKey: [
      QueryKey.Share,
      shareId,
      { limit: pagination.limit, ...(assetId ? { assetId } : { offset: pagination.offset }), sort, queryString },
    ],
    queryFn: async () => {
      const token = isAuthenticated ? await getAccessTokenSilently().catch(() => undefined) : undefined;
      return getPublicShare({ shareId, pagination, sort, queryString, assetId }, token);
    },
    retry: false,
    placeholderData: keepPreviousData,
    // This hook owns its own error UI (NotFound empty state / redirect to
    // /identifier), so a global toast on top would just be a redundant echo.
    meta: { suppressGlobalError: true },
    ...options,
  });

  // Derived from the live query error so it self-clears on a successful
  // refetch and avoids the strict-mode setState-in-queryFn footgun.
  const requiresAuth = shouldRedirectToIdentifier(shareError, isAuthenticated, authIsLoading);

  useEffect(() => {
    if (requiresAuth) {
      void push(buildIdentifierRedirectUrl());
    }
  }, [requiresAuth, push]);

  // Preserve the "no share = NotFound" contract: surface null (not undefined)
  // when the query errored into a known not-found / auth-required state.
  const share = data ?? (isPrivateShareAuthRequired(shareError) ? null : data);

  return {
    share,
    shareIsPending,
    refetchShare,
    shareIsFetching,
    shareIsLoading,
    shareIsFetched,
    shareError: isPrivateShareAuthRequired(shareError) ? null : shareError,
    requiresAuth,
  };
};

export const usePublicAsset = (
  params: {
    assetId: string;
    shareId: string;
  },
  form?: {
    reset: (data: any) => void;
  },
  options?: object,
) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authIsLoading } = useAuth0();
  const { push } = useRouter();
  const {
    data,
    isLoading: isPublicAssetLoading,
    isPending: isPublicAssetPending,
    isFetching: isPublicAssetFetching,
    error: assetError,
    refetch: refetchAsset,
  } = useQuery({
    queryKey: [QueryKey.Asset, params.assetId, params.shareId],
    queryFn:
      params.assetId && params.shareId
        ? async () => {
            const token = isAuthenticated ? await getAccessTokenSilently().catch(() => undefined) : undefined;
            const fetched = await getPublicAsset({ assetId: params.assetId, shareId: params.shareId }, token);
            form?.reset(fetched);
            return fetched;
          }
        : () => undefined,
    retry: false,
    ...options,
  });

  const requiresAuth = shouldRedirectToIdentifier(assetError, isAuthenticated, authIsLoading);

  useEffect(() => {
    if (requiresAuth) {
      void push(buildIdentifierRedirectUrl());
    }
  }, [requiresAuth, push]);

  useReloadOnAccessRevoked(data, assetError);

  const publicAsset = data ?? (isPrivateShareAuthRequired(assetError) ? null : data);

  return {
    publicAsset,
    refetchAsset,
    isPublicAssetFetching,
    isPublicAssetLoading,
    isPending: isPublicAssetPending,
    error: isPrivateShareAuthRequired(assetError) ? null : assetError,
    requiresAuth,
  };
};

/**
 * Aggregated common properties (tags intersection) for a multi-selection inside
 * a share — the guest counterpart to useMultipleAsset (DM-1409 fix). Token is
 * optional: anonymous on public shares, the viewer's token on private ones.
 */
export const usePublicMultipleAssets = (
  params: {
    shareId: string;
    assetIds: Array<string>;
  },
  options?: object,
) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const {
    data: publicMultipleAssets,
    isPending: isPublicMultipleAssetsPending,
    isFetching: isPublicMultipleAssetsFetching,
    error: publicMultipleAssetsError,
  } = useQuery({
    queryKey: [QueryKey.MultipleAssets, params.shareId, params.assetIds],
    queryFn: async () => {
      const token = isAuthenticated ? await getAccessTokenSilently().catch(() => undefined) : undefined;
      return getPublicMultipleAssets({ shareId: params.shareId, assetIds: params.assetIds }, token);
    },
    // Keep the prior intersection visible while a new selection refetches, so the
    // accordion never momentarily falls back to a single asset's full tag list.
    placeholderData: keepPreviousData,
    retry: false,
    ...options,
  });

  useReloadOnAccessRevoked(publicMultipleAssets, publicMultipleAssetsError);

  return {
    publicMultipleAssets,
    isPublicMultipleAssetsPending,
    isPublicMultipleAssetsFetching,
  };
};
